// Finance department dashboard. Reuses the same Financial Overview summary endpoints already
// built for Top Level (Receivables/Payables/Tax Liabilities), plus the shared Sales/Purchase
// metrics hook, Approvals, Quick access and Recent Transactions cards every dashboard uses.
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DollarSign, CreditCard, TrendingUp, ShoppingCart } from 'lucide-react';
import FrequentAccessCard from './FrequentAccessCard';
import RecentTransactionsCard from './RecentTransactionsCard';
import ApprovalsCard from './ApprovalsCard';
import SummaryCard from './SummaryCard';
import MonthComparisonCard, { MONTH_COMPARISON_TONES } from './MonthComparisonCard';
import GSTPositionCard from './GSTPositionCard';
import { useSalesPurchaseMetrics } from './useSalesPurchaseMetrics';
import {
    fetchClientOutStandingSummary,
    fetchVendorOutStandingSummary,
    fetchStockPurchaseConsolidateSummary,
    selectReceivablesSummary,
    selectPayablesSummary,
    selectTaxLiabilitiesSummary,
    selectReceivablesLoading,
    selectPayablesLoading,
    selectTaxLiabilitiesLoading,
} from '../../slices/dashboardSlice/financialOverviewSlice';

const FinanceDashboard = ({ onNavigate, linkFrequency, trackMenuUsage }) => {
    const { roleData, userData } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const receivablesSummary = useSelector(selectReceivablesSummary);
    const payablesSummary = useSelector(selectPayablesSummary);
    const taxLiabilitiesSummary = useSelector(selectTaxLiabilitiesSummary);
    const receivablesLoading = useSelector(selectReceivablesLoading);
    const payablesLoading = useSelector(selectPayablesLoading);
    const taxLiabilitiesLoading = useSelector(selectTaxLiabilitiesLoading);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;
    const groupId = userData?.groupId || userData?.GroupId || 0;
    const ccCodes = userData?.ccCodes || '';

    const {
        currentMonthSales, currentMonthPurchase,
        previousMonthSales, previousMonthPurchase,
        salesGrowth, purchaseGrowth,
    } = useSalesPurchaseMetrics(roleId, userId);

    useEffect(() => {
        dispatch(fetchClientOutStandingSummary({ roleId, userId }));
        dispatch(fetchVendorOutStandingSummary({ roleId, userId }));
        dispatch(fetchStockPurchaseConsolidateSummary({ roleId, userId }));
    }, [dispatch, roleId, userId]);

    return (
        <div className="space-y-6">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-[#0d1b5e] to-[#0a1240] dark:from-[#0a1240] dark:to-gray-900 rounded-lg p-6 text-white transition-colors">
                <h1 className="text-2xl font-bold mb-2">Welcome Back!</h1>
                <p className="text-orange-200 dark:text-orange-300">Employee: {userData?.firstName} | Role: {userData?.roleCode}</p>
            </div>

            {/* Sales / Purchase / Approvals + Outstanding & GST on the left, Quick access as a sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
                <div className="lg:col-span-3 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
                        <MonthComparisonCard
                            title="Sales this month"
                            icon={TrendingUp}
                            tone={MONTH_COMPARISON_TONES.sales}
                            amount={currentMonthSales}
                            growth={salesGrowth}
                            unfavorable={false}
                            currentValue={currentMonthSales}
                            previousValue={previousMonthSales}
                        />
                        <MonthComparisonCard
                            title="Purchase this month"
                            icon={ShoppingCart}
                            tone={MONTH_COMPARISON_TONES.purchase}
                            amount={currentMonthPurchase}
                            growth={purchaseGrowth}
                            unfavorable
                            currentValue={currentMonthPurchase}
                            previousValue={previousMonthPurchase}
                        />
                        <ApprovalsCard roleId={roleId} userId={userId} ccCodes={ccCodes} groupId={groupId} />
                    </div>

                    {/* Outstanding — Receivables & Payables (each with an age-bucket proportion bar) + GST position */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Outstanding</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
                            <SummaryCard
                                title="Receivables"
                                subtitle="Client outstanding by age"
                                icon={DollarSign}
                                tone="green"
                                loading={receivablesLoading}
                                data={receivablesSummary}
                                total={receivablesSummary?.TotalBalance}
                                showBar
                                rows={[
                                    { label: '0 – 90 days', value: receivablesSummary?.InvoiceBalBelow, dot: 'bg-green-500' },
                                    { label: '91 – 120 days', value: receivablesSummary?.InvoiceBalBetween91to120, dot: 'bg-yellow-500' },
                                    { label: '121 – 180 days', value: receivablesSummary?.InvoiceBalBetween121to180, dot: 'bg-orange-500' },
                                    { label: 'Above 180 days', value: receivablesSummary?.InvoiceBalAbove, dot: 'bg-red-500' },
                                    { divider: true },
                                    { label: 'Retention', value: receivablesSummary?.RetentionBalance, dot: 'bg-blue-400' },
                                    { label: 'Hold', value: receivablesSummary?.HoldBalance, dot: 'bg-gray-400' },
                                ]}
                            />

                            <SummaryCard
                                title="Payables"
                                subtitle="Vendor outstanding by age"
                                icon={CreditCard}
                                tone="red"
                                loading={payablesLoading}
                                data={payablesSummary}
                                total={payablesSummary?.TotalBalance}
                                showBar
                                rows={[
                                    { label: '0 – 30 days', value: payablesSummary?.Below30Days, dot: 'bg-green-500' },
                                    { label: '31 – 60 days', value: payablesSummary?.Between31And60Days, dot: 'bg-lime-500' },
                                    { label: '61 – 90 days', value: payablesSummary?.Between61And90Days, dot: 'bg-yellow-500' },
                                    { label: '91 – 120 days', value: payablesSummary?.Between91And120Days, dot: 'bg-orange-500' },
                                    { label: 'Above 120 days', value: payablesSummary?.Above120Days, dot: 'bg-red-500' },
                                    { divider: true },
                                    { label: 'Retention', value: payablesSummary?.RetentionBalance, dot: 'bg-blue-400' },
                                    { label: 'Hold', value: payablesSummary?.HoldBalance, dot: 'bg-gray-400' },
                                ]}
                            />

                            <GSTPositionCard
                                loading={taxLiabilitiesLoading}
                                summary={taxLiabilitiesSummary}
                            />
                        </div>
                    </div>

                    {/* Today's Recent Activity — kept alongside Outstanding so its width matches */}
                    <RecentTransactionsCard roleId={roleId} userId={userId} />
                </div>

                <div className="lg:col-span-1 space-y-4">
                    <FrequentAccessCard
                        roleData={roleData}
                        linkFrequency={linkFrequency}
                        trackMenuUsage={trackMenuUsage}
                        onNavigate={onNavigate}
                        extraSection={{
                            title: 'Reports & Statements',
                            keywords: ['ledger', 'transaction log', 'account status', 'overall status', 'gst'],
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default FinanceDashboard;
